import { Icon } from './Icon';
import type { Task } from '../api';

/**
 * Which task this one is part of.
 *
 * Two questions come apart along the line `Icon.tsx` draws — an icon may replace a label when its
 * set of values is closed and small, and there is deliberately no icon for a goal name because
 * there is no fixed set:
 *
 * | Question | Set | Drawn as |
 * | --- | --- | --- |
 * | Is this a subtask? | closed, repeats on many rows | an arrow |
 * | Whose subtask? | open — any task title | text, as a goal name is |
 *
 * So: an arrow always, the name where there is room. One control, not two, and its accessible
 * name is always the whole sentence — a narrow screen costs the name, never the fact that there
 * is a parent to ask about. A chip carrying the name alone loses both halves at once, since
 * `↳ Q3 pl…` tells you nothing.
 *
 * Hover is a bonus and never the route: the full title is the `title` attribute, which a pointer
 * gets free and a phone never sees.
 */
export function ParentLink({
  parent,
  showName = true,
  onOpen,
}: {
  parent: NonNullable<Task['parent']>;
  /** False where a name cannot fit — a timeline block, or below the two-column breakpoint. */
  showName?: boolean;
  onOpen?: (id: string) => void;
}) {
  const label = `Part of ${parent.title}`;
  return (
    <button
      type="button"
      className="link parent-link"
      data-testid="parent-link"
      aria-label={label}
      title={label}
      onClick={
        onOpen &&
        ((e) => {
          // The row underneath opens the subtask; this opens its parent. Without this the click
          // does both, and the one that lands last wins.
          e.stopPropagation();
          onOpen(parent.id);
        })
      }
      disabled={!onOpen}
    >
      <Icon name="subtask-of" size={12} />
      {showName && <span className="parent-name">{parent.title}</span>}
    </button>
  );
}
